/**
 * Constants and Configuration for Student Profile Component
 */

/**
 * Grade Mapping - Maps numeric grade values to their display names
 */
export const GRADE_MAPPING = {
    "-4": "Day Care Unit",
    "-3": "Nursery",
    "-2": "LKG",
    "-1": "UKG",
    "1": "Grade 1",
    "2": "Grade 2",
    "3": "Grade 3",
    "4": "Grade 4",
    "5": "Grade 5",
    "6": "Grade 6",
    "7": "Grade 7",
    "8": "Grade 8",
    "9": "Grade 9",
    "10": "Grade 10",
    "11": "Grade 11",
    "12": "Grade 12"
};

/**
 * Get grade display name from numeric value
 * @param {string|number} gradeValue - Numeric grade value
 * @returns {string} - Display name of the grade or the original value if not found
 */
export const getGradeDisplayName = (gradeValue) => {
    if (gradeValue === null || gradeValue === undefined || gradeValue === '') {
        return '-';
    }
    
    // Convert to string for lookup
    const gradeKey = String(gradeValue);
    
    // Return mapped value or original value if not found in mapping
    return GRADE_MAPPING[gradeKey] || gradeValue;
};

/**
 * Get default state for Student Profile component
 */
export const getDefaultState = () => ({
    activeIndex: 0,
    studentId: '',
    isLoading: false,
    studentData: {},
    showDetailView: false,
    activeDetailView: 'personalInfo', // personalInfo, siblingsInfo, familyDetails, transportInfo, attendanceInfo
    activeMenu: 'studentDetails', // studentDetails, studentForms, studentLog, academics, certificates, finance
    activeSubMenu: 'personalInfo', // Track active submenu for highlighting
    activeAttendanceTab: 'regular', // regular, rfid, transport, periodwise, professional
    attendanceData: {}, // Will be populated from API
    // Daily Attendance Dialog
    showDailyAttendanceDialog: false,
    selectedMonth: null,
    selectedAttendanceType: null,
    dailyAttendanceData: [],
    // Student Details Section
    showStudentDetailsView: true, // Show by default
    studentDetailsMinimized: false, // Controls minimize/maximize state of student details left panel
    // Finance Section
    showFinanceView: false,
    activeFinanceView: 'feeDetails', // feeDetails, viewTransactions, viewChequePayments, etc.
    financeMinimized: false, // Controls minimize/maximize state of finance left panel
    activeFeeDetailsTab: 'feetypeWise', // feetypeWise, installmentWise, monthWise, feesWithRefund, concessions, unassignFees
    activeTransactionsTab: 'transactions', // transactions, feeAccount, paymentMode, aging, onlinePayments, otherFees, chequePayments, cancelled, advances
    // Feetype Wise Fee Data
    feetypeWiseFeeData: null, // Will be populated from API (transformed)
    feetypeWiseFeeLoading: false, // Loading state for feetype wise fee data
    // Raw API data for other views
    feeTermsRawData: null, // Raw API response data (agg array) - used for installment wise, month wise, etc.
    // Fee History Data (Unassign Fees)
    feeHistoryData: null, // Will be populated from fee-history API
    feeHistoryLoading: false, // Loading state for fee history data
    // Transactions Data (View Transactions, Cheque Payments, etc.)
    transactionsData: null, // Will be populated from fee-collections API
    transactionsLoading: false, // Loading state for transactions data
    // Cancelled Transactions Data
    cancelledTransactionsData: null, // Will be populated from cancelled-transactions API
    cancelledTransactionsLoading: false // Loading state for cancelled transactions data
});

/**
 * Get menu items configuration
 * @param {string} activeSubMenu - Currently active submenu
 * @param {Function} setState - setState function from component
 */
export const getMenuItems = (activeSubMenu, setState) => ({
    studentDetails: [
        {
            label: 'Personal Info',
            icon: 'pi pi-user',
            className: activeSubMenu === 'personalInfo' ? 'active-submenu' : '',
            command: () => setState({ showStudentDetailsView: true, activeDetailView: 'personalInfo', activeMenu: 'studentDetails', activeSubMenu: 'personalInfo' })
        },
        {
            label: 'Siblings Info',
            icon: 'pi pi-users',
            className: activeSubMenu === 'siblingsInfo' ? 'active-submenu' : '',
            command: () => setState({ showStudentDetailsView: true, activeDetailView: 'siblingsInfo', activeMenu: 'studentDetails', activeSubMenu: 'siblingsInfo' })
        },
        {
            label: 'Family Details',
            icon: 'pi pi-users',
            className: activeSubMenu === 'familyDetails' ? 'active-submenu' : '',
            command: () => setState({ showStudentDetailsView: true, activeDetailView: 'familyDetails', activeMenu: 'studentDetails', activeSubMenu: 'familyDetails' })
        },
        {
            label: 'Transport Info',
            icon: 'fa fa-bus',
            className: activeSubMenu === 'transportInfo' ? 'active-submenu' : '',
            command: () => setState({ showStudentDetailsView: true, activeDetailView: 'transportInfo', activeMenu: 'studentDetails', activeSubMenu: 'transportInfo' })
        },
        {
            label: 'Attendance Info',
            icon: 'pi pi-calendar',
            className: activeSubMenu === 'attendanceInfo' ? 'active-submenu' : '',
            command: () => setState({ showStudentDetailsView: true, activeDetailView: 'attendanceInfo', activeMenu: 'studentDetails', activeSubMenu: 'attendanceInfo' })
        }
    ],
    studentForms: [
        { label: 'Application Form', icon: 'pi pi-file', className: activeSubMenu === 'applicationForm' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'studentForms', activeSubMenu: 'applicationForm' }) },
        { label: 'Medical Form', icon: 'pi pi-heart', className: activeSubMenu === 'medicalForm' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'studentForms', activeSubMenu: 'medicalForm' }) },
        { label: 'Consent Forms', icon: 'pi pi-check-square', className: activeSubMenu === 'consentForms' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'studentForms', activeSubMenu: 'consentForms' }) }
    ],
    studentLog: [
        { label: 'Attendance Log', icon: 'pi pi-calendar', className: activeSubMenu === 'attendanceLog' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'studentLog', activeSubMenu: 'attendanceLog' }) },
        { label: 'Behavior Log', icon: 'pi pi-star', className: activeSubMenu === 'behaviorLog' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'studentLog', activeSubMenu: 'behaviorLog' }) },
        { label: 'Activity Log', icon: 'pi pi-list', className: activeSubMenu === 'activityLog' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'studentLog', activeSubMenu: 'activityLog' }) }
    ],
    academics: [
        { label: 'Grades', icon: 'pi pi-chart-line', className: activeSubMenu === 'grades' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'academics', activeSubMenu: 'grades' }) },
        { label: 'Assignments', icon: 'pi pi-book', className: activeSubMenu === 'assignments' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'academics', activeSubMenu: 'assignments' }) },
        { label: 'Exams', icon: 'pi pi-pencil', className: activeSubMenu === 'exams' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'academics', activeSubMenu: 'exams' }) },
        { label: 'Report Cards', icon: 'pi pi-file-pdf', className: activeSubMenu === 'reportCards' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'academics', activeSubMenu: 'reportCards' }) }
    ],
    certificates: [
        { label: 'Transfer Certificate', icon: 'pi pi-file', className: activeSubMenu === 'transferCertificate' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'certificates', activeSubMenu: 'transferCertificate' }) },
        { label: 'Bonafide Certificate', icon: 'pi pi-file', className: activeSubMenu === 'bonafideCertificate' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'certificates', activeSubMenu: 'bonafideCertificate' }) },
        { label: 'Character Certificate', icon: 'pi pi-file', className: activeSubMenu === 'characterCertificate' ? 'active-submenu' : '', command: () => setState({ activeMenu: 'certificates', activeSubMenu: 'characterCertificate' }) }
    ],
    finance: [
        // { 
        //     label: 'Receipt Entry', 
        //     icon: 'pi pi-receipt', 
        //     className: activeSubMenu === 'receiptEntry' ? 'active-submenu' : '', 
        //     command: () => setState({ showFinanceView: true, activeFinanceView: 'receiptEntry', activeMenu: 'finance', activeSubMenu: 'receiptEntry' }) 
        // },
        { 
            label: 'Fee Details', 
            icon: 'pi pi-money-bill', 
            className: activeSubMenu === 'feeDetails' ? 'active-submenu' : '', 
            command: () => setState({ showFinanceView: true, activeFinanceView: 'feeDetails', activeMenu: 'finance', activeSubMenu: 'feeDetails' }) 
        },
        { 
            label: 'View Transactions', 
            icon: 'pi pi-list', 
            className: activeSubMenu === 'viewTransactions' ? 'active-submenu' : '', 
            command: () => setState({ showFinanceView: true, activeFinanceView: 'viewTransactions', activeMenu: 'finance', activeSubMenu: 'viewTransactions' }) 
        },
        // { 
        //     label: 'Fee Management', 
        //     icon: 'pi pi-cog', 
        //     className: activeSubMenu === 'feeManagement' ? 'active-submenu' : '', 
        //     command: () => setState({ showFinanceView: true, activeFinanceView: 'feeManagement', activeMenu: 'finance', activeSubMenu: 'feeManagement' }) 
        // },
        { 
            label: 'View Cheque Payments', 
            icon: 'pi pi-credit-card', 
            className: activeSubMenu === 'viewChequePayments' ? 'active-submenu' : '', 
            command: () => setState({ showFinanceView: true, activeFinanceView: 'viewChequePayments', activeMenu: 'finance', activeSubMenu: 'viewChequePayments' }) 
        },
        { 
            label: 'Transport Fee Management', 
            icon: 'fa fa-bus', 
            className: activeSubMenu === 'transportFeeManagement' ? 'active-submenu' : '', 
            command: () => setState({ showFinanceView: true, activeFinanceView: 'transportFeeManagement', activeMenu: 'finance', activeSubMenu: 'transportFeeManagement' }) 
        },
        { 
            label: 'Cheque Bounce Charges', 
            icon: 'pi pi-exclamation-triangle', 
            className: activeSubMenu === 'chequeBounceCharges' ? 'active-submenu' : '', 
            command: () => setState({ showFinanceView: true, activeFinanceView: 'chequeBounceCharges', activeMenu: 'finance', activeSubMenu: 'chequeBounceCharges' }) 
        },
        { 
            label: 'Post Dated Cheque Payments', 
            icon: 'pi pi-calendar-plus', 
            className: activeSubMenu === 'postDatedChequePayments' ? 'active-submenu' : '', 
            command: () => setState({ showFinanceView: true, activeFinanceView: 'postDatedChequePayments', activeMenu: 'finance', activeSubMenu: 'postDatedChequePayments' }),
        },
        { 
            label: 'Advance Payments', 
            icon: 'pi pi-forward', 
            className: activeSubMenu === 'advancePayments' ? 'active-submenu' : '', 
            command: () => setState({ showFinanceView: true, activeFinanceView: 'advancePayments', activeMenu: 'finance', activeSubMenu: 'advancePayments' }) 
        },
        // { 
        //     label: 'Edit Fee Amount', 
        //     icon: 'pi pi-pencil', 
        //     className: activeSubMenu === 'editFeeAmount' ? 'active-submenu' : '', 
        //     command: () => setState({ showFinanceView: true, activeFinanceView: 'editFeeAmount', activeMenu: 'finance', activeSubMenu: 'editFeeAmount' }) 
        // },
        // { 
        //     label: 'Student Deposits / Transactions', 
        //     icon: 'pi pi-database', 
        //     className: activeSubMenu === 'studentDepositsTransactions' ? 'active-submenu' : '', 
        //     command: () => setState({ showFinanceView: true, activeFinanceView: 'studentDepositsTransactions', activeMenu: 'finance', activeSubMenu: 'studentDepositsTransactions' }) 
        // },
        // { 
        //     label: 'Loan Payments', 
        //     icon: 'pi pi-wallet', 
        //     className: activeSubMenu === 'loanPayments' ? 'active-submenu' : '', 
        //     command: () => setState({ showFinanceView: true, activeFinanceView: 'loanPayments', activeMenu: 'finance', activeSubMenu: 'loanPayments' }) 
        // }
    ]
});
